<template>
    <div v-if="productRating?.totalReviews" class="rating">
        <div class="rating-container row pt-4 d-flex align-items-start">
            <div class="col-6 col-md-4 text-center d-flex justify-content-center align-items-center">
                <div>
                    <strong class="fs-23" data-cy="average-rating">{{ formatNumber(productRating.averageRating) }}</strong>
                    <div class="d-flex justify-content-center my-2">
                        <ObjectsRating :rating="productRating.averageRating" :box="true" size="size-l" />
                    </div>
                    <div class="recommendPercent" data-cy="recommend-percent">
                        <img
                            v-lazy-load
                            :alt="$t('productDetail.rating.recommend')"
                            src="/nuxt-assets/images/lazy-load-error.svg"
                            data-src="/nuxt-assets/images/icons/like.svg"
                            class="recommendPercent__img"
                            width="10"
                            height="10"
                        />
                        <p class="recommendPercent__text">
                            {{ $t('rating.percentCustomersRecommended', productRating.recommendPercentage) }}
                        </p>
                    </div>
                    <div class="ratingUsers" data-cy="rating-users" @click="sendActiveTab('tab-reviews')">
                        {{ $t('rating.users', productRating.totalReviews) }}
                    </div>
                    <a
                        class="btn btn-outline-primary d-none d-md-inline-block"
                        data-cy="add-review"
                        href="#modal-form-review"
                        @click.prevent="$bus.$emit('showModal', 'modal-form-review')"
                    >
                        {{ $t('rating.write') }}
                    </a>
                </div>
            </div>

            <div class="col-6 col-md-3 text-nowrap align-content-center">
                <Recommend :items="formatProductRating(productRating)" count-class-name="recommend__count--silver" />
            </div>

            <div class="col-12 mt-3 text-center d-md-none">
                <a class="btn btn-outline-primary" href="#modal-form-review" @click.prevent="$bus.$emit('showModal', 'modal-form-review')">
                    {{ $t('rating.write') }}
                </a>
            </div>

            <div v-if="questionRatings.length" class="col-12 col-md-5 col-lg-4 mt-4 mt-md-0 mb-3 mb-md-0 mx-auto max-450">
                <Recommend :items="questionRating(questionRatings)" class="recommend--question" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { useNuxtApp } from 'nuxt/app'
import { PropType } from 'vue'
import Recommend from '~/components/elements/Recommend.vue'
import formatProductRating from '~/utils/formatProductRating'
import ProductRating from '~/models/Product/ProductRating'
import RatingProgressBar from '~/models/Product/RatingProgressBar'
import CommentQuestionRating from '~/models/Product/CommentQuestionRating'

export default {
    components: {
        Recommend,
    },
    props: {
        questionRatings: {
            type: Array as PropType<CommentQuestionRating[]>,
            required: true,
        },
        productRating: {
            type: Object as PropType<ProductRating>,
            required: true,
        },
    },
    methods: {
        formatNumber(number: number): string {
            const numberFinal = number.toString()
            return numberFinal.slice(0, 3)
        },

        getRatingPercentage({ averageValue }: { averageValue: number }): number {
            return Math.round((averageValue / 5) * 100)
        },

        sendActiveTab(nameTab: string): void {
            useNuxtApp().$bus.$emit('setActiveTab', nameTab)
        },

        questionRating(questionRatings: CommentQuestionRating[]): RatingProgressBar[] {
            const ratingsItems: RatingProgressBar[] = []

            questionRatings.forEach((questionRating: CommentQuestionRating) => {
                if (questionRating.averageValue !== 0) {
                    ratingsItems.push({
                        name: questionRating.text,
                        progress: this.getRatingPercentage(questionRating),
                        count: `${this.getRatingPercentage(questionRating)}%`,
                    })
                }
            })

            return ratingsItems
        },

        formatProductRating(productRating: ProductRating): RatingProgressBar[] {
            return formatProductRating(productRating)
        },
    },
}
</script>

<style lang="scss" scoped>
.add-rating {
    width: 150px;
    height: 32px;
    background: url('/nuxt-assets/icons/sprite-rating.svg') repeat-x 0 0;

    span {
        position: relative;
        top: 0;
        left: 0;
        display: block;
        width: 0;
        height: 30px;
        background: url('/nuxt-assets/icons/sprite-rating.svg') repeat-x 0 -60px;
    }

    input {
        position: absolute;
        opacity: 0;

        &.star-5:checked ~ span {
            width: 100%;
        }

        &.star-4:checked ~ span {
            width: 80%;
        }

        &.star-3:checked ~ span {
            width: 60%;
        }

        &.star-2:checked ~ span {
            width: 40%;
        }

        &.star-1:checked ~ span {
            width: 20%;
        }
    }

    label {
        display: block;
        width: 30px;
        height: 30px;
        margin: 0;
        padding: 0;
        text-indent: -999em;
        float: left;
        position: relative;
        z-index: 10;
        background: transparent;
        cursor: pointer;
    }
}

.add-rating-points {
    input {
        display: none;
    }

    label {
        display: inline-block;
        text-indent: -999em;
        position: relative;
        opacity: 0.1;
        width: 16px;
        max-width: 21px;
        height: 11px;
        margin: 0;
        transition: ease 0.2s;

        &::after {
            content: '';
            width: 18px;
            height: 11px;
            background-color: $color-purple;
            border-radius: 4px;
            display: block;
        }

        &.active {
            opacity: 1;
        }
    }
}

.rating-progress-bar {
    width: 100%;
    height: 8px;
    background-color: $color-grey;
    position: relative;
    border-radius: 4px;
    overflow: hidden;

    &__active {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background-color: $color-purple;
    }
}

.star-xs__container {
    background-color: $color-yellow-light;
    border-radius: 50px;
    padding: 4px 16px;
}

.star-xs__container__item {
    margin: 0 2px;
}

.top-0 {
    top: 0;
}

.left-0 {
    left: 0;
}

.ratings-count .count {
    font-size: 12px;
}

.product-detail__review-bar-star .ico {
    margin-bottom: 0;
}

.product-detail__review-number {
    width: 15px;
}

.product-detail__number-rate {
    display: block;
    text-align: center;
    width: 20px;
}

.recommendPercent {
    background-color: $color-green-light;
    border-radius: 50px;
    color: $color-green-dark;
    padding: 4px 17px;
    display: flex;
}

.recommendPercent__text {
    font-weight: 600;
    margin: 3px 0 3px 5px;
}

.ratingUsers {
    font-size: 12px;
    margin: 8px 0;
    cursor: pointer;

    &:hover {
        @media (min-width: 1024px) {
            text-decoration: underline;
        }
    }
}

.product-detail__number-rate {
    font-size: 12px;
}

.totalCount {
    cursor: pointer;

    &:hover {
        @media (min-width: 1024px) {
            text-decoration: underline;
        }
    }
}

.max-450 {
    max-width: 450px;
}
</style>
